'use client';

import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  PRODUCTS,
  KIBBLE_TYPE_LABELS,
  DOG_SIZE_LABELS,
  type DogSize,
  type KibbleType,
} from '@/lib/mockData';
import type { Filters } from './SearchResultsContent';

const ALL_BRANDS = [...new Set(PRODUCTS.map((p) => p.brand))].sort();
const ALL_SIZES: DogSize[] = ['XS', 'S', 'M', 'L', 'XL'];
const ALL_TYPES: KibbleType[] = ['standard', 'premium', 'veterinary', 'bio', 'grain-free'];

interface Props {
  filters: Filters;
  setFilters: (f: Filters) => void;
}

function FilterSection({
  title,
  children,
  defaultOpen = true,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-orange-100 py-4 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between text-left"
      >
        <span className="font-display font-semibold text-sm text-gray-800">{title}</span>
        {open ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
      </button>
      {open && <div className="mt-3">{children}</div>}
    </div>
  );
}

export default function FilterSidebar({ filters, setFilters }: Props) {
  function toggleBrand(brand: string) {
    const next = filters.brands.includes(brand)
      ? filters.brands.filter((b) => b !== brand)
      : [...filters.brands, brand];
    setFilters({ ...filters, brands: next });
  }

  function toggleSize(size: DogSize) {
    const next = filters.dogSizes.includes(size)
      ? filters.dogSizes.filter((s) => s !== size)
      : [...filters.dogSizes, size];
    setFilters({ ...filters, dogSizes: next });
  }

  function toggleType(type: KibbleType) {
    const next = filters.kibbleTypes.includes(type)
      ? filters.kibbleTypes.filter((k) => k !== type)
      : [...filters.kibbleTypes, type];
    setFilters({ ...filters, kibbleTypes: next });
  }

  return (
    <div className="bg-white rounded-2xl border border-orange-100 shadow-sm p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-display font-bold text-gray-900">Filtres</h3>
        <button
          onClick={() =>
            setFilters({ brands: [], dogSizes: [], kibbleTypes: [], maxPrice: 200, freeShippingOnly: false })
          }
          className="text-xs text-orange-600 hover:text-orange-700 font-semibold transition-colors"
        >
          Tout effacer
        </button>
      </div>

      {/* Dog size */}
      <FilterSection title="Taille du chien">
        <div className="flex flex-wrap gap-2">
          {ALL_SIZES.map((size) => (
            <button
              key={`filter-size-${size}`}
              onClick={() => toggleSize(size)}
              className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-all duration-150 active:scale-95 ${
                filters.dogSizes.includes(size)
                  ? 'bg-teal-600 text-white border-teal-600' :'bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100'
              }`}
            >
              {size}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-2">
          {filters.dogSizes.length > 0
            ? filters.dogSizes.map((s) => DOG_SIZE_LABELS[s]).join(', ')
            : 'Toutes les tailles'}
        </p>
      </FilterSection>

      {/* Kibble type */}
      <FilterSection title="Type de croquettes">
        <div className="space-y-2">
          {ALL_TYPES.map((type) => (
            <label
              key={`filter-type-${type}`}
              className="flex items-center gap-2.5 cursor-pointer group"
            >
              <input
                type="checkbox"
                checked={filters.kibbleTypes.includes(type)}
                onChange={() => toggleType(type)}
                className="w-4 h-4 rounded border-orange-300 text-orange-500 focus:ring-orange-400 accent-orange-500"
              />
              <span className="text-sm text-gray-700 group-hover:text-orange-600 transition-colors">
                {KIBBLE_TYPE_LABELS[type]}
              </span>
            </label>
          ))}
        </div>
      </FilterSection>

      {/* Brand */}
      <FilterSection title="Marque">
        <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
          {ALL_BRANDS.map((brand) => (
            <label key={`filter-brand-${brand.replace(/\s+/g, '-')}`} className="flex items-center gap-2.5 cursor-pointer group">
              <input
                type="checkbox"
                checked={filters.brands.includes(brand)}
                onChange={() => toggleBrand(brand)}
                className="w-4 h-4 rounded border-orange-300 accent-orange-500"
              />
              <span className="text-sm text-gray-700 group-hover:text-orange-600 transition-colors">
                {brand}
              </span>
            </label>
          ))}
        </div>
      </FilterSection>

      {/* Price range */}
      <FilterSection title="Prix maximum">
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">0 €</span>
            <span className="font-semibold text-orange-600 tabular-nums">{filters.maxPrice} €</span>
          </div>
          <input
            type="range"
            min={5}
            max={200}
            step={5}
            value={filters.maxPrice}
            onChange={(e) => setFilters({ ...filters, maxPrice: +e.target.value })}
            className="w-full accent-orange-500"
          />
          <div className="flex flex-wrap gap-2 mt-2">
            {[30, 60, 100, 200].map((v) => (
              <button
                key={`price-preset-${v}`}
                onClick={() => setFilters({ ...filters, maxPrice: v })}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                  filters.maxPrice === v
                    ? 'bg-orange-500 text-white border-orange-500' :'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100'
                }`}
              >
                {v === 200 ? 'Tous' : `≤ ${v} €`}
              </button>
            ))}
          </div>
        </div>
      </FilterSection>

      {/* Free shipping */}
      <FilterSection title="Livraison" defaultOpen={false}>
        <label className="flex items-center gap-2.5 cursor-pointer group">
          <input
            type="checkbox"
            checked={filters.freeShippingOnly}
            onChange={(e) => setFilters({ ...filters, freeShippingOnly: e.target.checked })}
            className="w-4 h-4 rounded border-orange-300 accent-orange-500"
          />
          <span className="text-sm text-gray-700 group-hover:text-orange-600 transition-colors">
            Livraison gratuite uniquement
          </span>
        </label>
      </FilterSection>
    </div>
  );
}